import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Retry once on transient failures (5xx + network errors) so brief
// backend restarts / cold-starts don't surface a raw "Network Error"
// to the user. Applied uniformly to every method — login is a POST
// but we still want it retried because the previous attempt never
// reached the server. The 600ms backoff is small enough to feel
// instant to the user but long enough to clear a uvicorn reload.
const TRANSIENT_STATUS = new Set([502, 503, 504]);

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const config = error?.config;
    if (!config || config.__retryCount >= 1) return Promise.reject(error);
    const status = error?.response?.status;
    const isNetworkErr = !error?.response;
    const transient = isNetworkErr || TRANSIENT_STATUS.has(status);
    if (!transient) return Promise.reject(error);
    config.__retryCount = (config.__retryCount || 0) + 1;
    await new Promise((res) => setTimeout(res, 600));
    return api(config);
  }
);

export function extractError(err, fallback = "Something went wrong. Please try again.") {
  // Friendlier copy for transient network / gateway errors that make it
  // past the retry — usually a longer backend restart or a bad connection.
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return "We couldn't reach the server. Please check your connection and try again in a moment.";
  }
  const status = err?.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return "The server is briefly unavailable. Please try again in a few seconds.";
  }
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" · ");
  if (d?.msg) return d.msg;
  return String(d);
}
